import React, { useState } from 'react';
import moment from 'moment';
import { AccessTime, Close, PendingActions } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

import './TaskList.scss';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { commentActionEnum } from 'redux/conductInspectionSlice';
import { StartInspectionModal } from 'pages/ScheduledInspections/StartInspectionModal';
import { useGetInspectionByIdMutation } from 'redux/services/inspection';

export type TaskListProps = {
  tasks: any[];
};

export const TaskList: React.FC<TaskListProps> = ({ tasks }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [getInspectionDetails, { data, reset }] =
    useGetInspectionByIdMutation();
  const [selectedTask, setSelectedTask] = useState<any>(null);

  const getTaskClickHandler = (task: any) => () => {
    if (task.type === 'SCHEDULE_INSPECTION') {
      reset();
      getInspectionDetails({
        projectId: task.projectId,
        id: task.inspectionId,
      });
    } else {
      setSelectedTask(task);
    }
  };

  const getStatus = () => {
    if (selectedTask?.currentStatus === commentActionEnum.Approve) {
      return t('text_approved');
    } else if (selectedTask?.currentStatus === commentActionEnum.Disapprove) {
      return t('text_disapproved');
    } else if (selectedTask?.currentStatus === commentActionEnum.Scheduled) {
      return t('btn_scheduled');
    } else if (selectedTask?.currentStatus === commentActionEnum.Closed) {
      return t('btn_closed');
    }

    return selectedTask?.currentStatus === commentActionEnum.TaskRaised
      ? t('text_task_raised')
      : t('text_sent_for_approval');
  };

  const handleOpenTask = () => {
    const task = selectedTask;

    if (task.type === 'INSPECTION') {
      navigate(
        `/project/${task.projectId}/inspection/${task.inspectionId}/review?taskId=${task._id}&path=${location.pathname}`,
        { replace: true },
      );
    } else if (task.type === 'OBSERVATION') {
      navigate(
        `/project/${task.projectId}/observation/${task.observationId}/review?taskId=${task._id}&path=${location.pathname}`,
        { replace: true },
      );
    } else {
      navigate(
        `/project/${task.projectId}/incident/${task.incidentId}/review?taskId=${task._id}&path=${location.pathname}`,
        { replace: true },
      );
    }

    setSelectedTask(null);
  };

  const handleClose = () => setSelectedTask(null);
  const handleCloseStartInspection = () => reset();

  return (
    <>
      <ul className="task-list gap-2">
        {tasks?.length
          ? tasks.map((task: any) => (
              <li
                className={`d-flex flex-column task task-${
                  task.type?.toLowerCase() ?? ''
                }`}
                key={task._id}
                onClick={getTaskClickHandler(task)}
              >
                <div className="d-flex justify-space-between flex-nowrap align-items-center mb-2">
                  <PendingActions fontSize="small" color="warning" />
                  <span className="task-name mx-1">{task.name}</span>
                  <span className="task-type">
                    {task.type === 'SCHEDULE_INSPECTION'
                      ? 'SCHEDULED INSPECTION'
                      : task.type}
                  </span>
                </div>

                <div>
                  <AccessTime fontSize="small" className="date-icon" />
                  <span className="date mx-1">
                    {moment(task.updated_at).format('DD-MM-yyyy')}
                  </span>
                </div>
                <div className="left-gutter position-absolute h-100" />
              </li>
            ))
          : null}
      </ul>
      {!tasks?.length && (
        <div className="d-flex w-100 h-100 flex-column p-2 align-items-center justify-content-center">
          <div className="no-task-placeholder-img" />
          <span className="h6">{t('placeholder_text_no_pending_tasks')}</span>
        </div>
      )}
      {!!data && (
        <StartInspectionModal
          inspection={data}
          open
          onClose={handleCloseStartInspection}
        />
      )}
      <Dialog
        open={!!selectedTask}
        className="open-task-dialog"
        PaperProps={{
          style: {
            padding: 0,
            minWidth: 300,
          },
        }}
        onClose={handleClose}
      >
        <DialogActions className="dialog-actions d-flex justify-content-between w-100">
          <DialogTitle className="p-1" color="inherit">
            {t('task_modal_title')}
          </DialogTitle>
          <IconButton className="close-btn" onClick={handleClose}>
            <Close color="inherit" />
          </IconButton>
        </DialogActions>
        <DialogContent>
          <div className="d-flex flex-column mb-2">
            <div className="d-flex justify-content-between mb-2">
              <PendingActions sx={{ marginRight: 1 }} color="warning" />
              <span className="form-value">{selectedTask?.name}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span>{t('text_task_type')}</span>
              <span className="form-value">{selectedTask?.type}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span>{t('text_current_status')}</span>
              <span className="form-value">{getStatus()}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span>{t('text_last_updated')}</span>
              <div>
                <AccessTime fontSize="small" className="date-icon" />
                <span className="date mx-1 form-value">
                  {moment(selectedTask?.updated_at).format('DD-MM-yyyy')}
                </span>
              </div>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span>{t('text_last_updated_by')}</span>
              <span className="form-value">{selectedTask?.updatedBy}</span>
            </div>
          </div>
          <div className="d-flex w-100 justify-content-center">
            <Button variant="contained" onClick={handleOpenTask}>
              {t('btn_open')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
